import { useQueries } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import type { ItemV2 } from '@/common/api/types/items';
import { itemsBulkFetchV2QueryOptions } from '@/common/queries/useGetItemsBulkFetchV2Query';

const CHILDREN_STALE_TIME = 5 * 60 * 1000;
const EMPTY_CHILDREN: ReadonlyMap<number, Partial<ItemV2>[]> = new Map();
const EMPTY_LOADING: ReadonlySet<number> = new Set();

/**
 * Lazily fetch full bodies for the children of parents that are expanded, one
 * query per parent keyed by the parent's child-id list. Keying per parent means
 * a parent's children are fetched once and reused — scrolling a parent out and
 * back is a cache hit, and expanding a new parent fetches only its own children.
 * Fetching is gated on the parent being in the virtualized viewport.
 */
export const useTimelineLazyChildren = ({
  childIdsByParentId,
  expandedChildren,
  visibleParentIds,
  customFieldIds,
}: {
  childIdsByParentId: ReadonlyMap<number, number[]>;
  expandedChildren: Record<number, boolean>;
  visibleParentIds: ReadonlySet<number>;
  customFieldIds?: number[];
}): {
  childrenByParentId: ReadonlyMap<number, Partial<ItemV2>[]>;
  loadingParentIds: ReadonlySet<number>;
  isFetching: boolean;
} => {
  const expandedParents = useMemo(() => {
    const parents: { parentId: number; childIds: number[] }[] = [];
    for (const [parentId, childIds] of childIdsByParentId) {
      if (expandedChildren[parentId]) parents.push({ parentId, childIds });
    }
    return parents;
  }, [childIdsByParentId, expandedChildren]);

  // useQueries/combine runs on every render, so cache the derived Map/Set and rebuild
  // only when a dataUpdatedAt/childIds signature changes. Without this the returned
  // childrenByParentId reference churns every render and cascades a full item-tree
  // recompute downstream — do not collapse this back into useMemo(..., [results]).
  const prevChildrenSignatureRef = useRef<string | null>(null);
  const prevChildrenRef = useRef<ReadonlyMap<number, Partial<ItemV2>[]>>(EMPTY_CHILDREN);
  const prevLoadingSignatureRef = useRef<string | null>(null);
  const prevLoadingRef = useRef<ReadonlySet<number>>(EMPTY_LOADING);

  return useQueries({
    queries: expandedParents.map(({ parentId, childIds }) =>
      itemsBulkFetchV2QueryOptions(
        { itemIds: childIds, customFieldIds },
        CHILDREN_STALE_TIME,
        visibleParentIds.has(parentId),
      ),
    ),
    combine: (results) => {
      const childrenSignature = expandedParents
        .map(
          ({ parentId, childIds }, index) =>
            `${parentId}#${childIds.join(',')}:${results[index]?.dataUpdatedAt ?? 0}`,
        )
        .join('|');
      if (childrenSignature !== prevChildrenSignatureRef.current) {
        const children = new Map<number, Partial<ItemV2>[]>();
        expandedParents.forEach(({ parentId, childIds }, index) => {
          const itemsById = results[index]?.data?.itemsById;
          if (!itemsById) return;
          const loaded = childIds
            .map((id) => itemsById[String(id)])
            .filter((item): item is Partial<ItemV2> => item !== undefined);
          if (loaded.length > 0) children.set(parentId, loaded);
        });
        prevChildrenSignatureRef.current = childrenSignature;
        prevChildrenRef.current = children.size > 0 ? children : EMPTY_CHILDREN;
      }

      const loadingSignature = expandedParents
        .map(({ parentId }, index) => {
          const result = results[index];
          return `${parentId}:${result?.isFetching && result.data === undefined ? 1 : 0}`;
        })
        .join('|');
      if (loadingSignature !== prevLoadingSignatureRef.current) {
        const loading = new Set<number>();
        expandedParents.forEach(({ parentId }, index) => {
          const result = results[index];
          if (result?.isFetching && result.data === undefined) loading.add(parentId);
        });
        prevLoadingSignatureRef.current = loadingSignature;
        prevLoadingRef.current = loading.size > 0 ? loading : EMPTY_LOADING;
      }

      return {
        childrenByParentId: prevChildrenRef.current,
        loadingParentIds: prevLoadingRef.current,
        isFetching: results.some((result) => result.isFetching),
      };
    },
  });
};
